import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import pluralize from 'pluralize';

import { formatEnumValue } from '../../../../../../../util/text';
import {
  getFreePlan,
  SEARCH_SUBSCRIPTION_PLAN,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
  SEARCH_SUBSCRIPTION_PLAN_PRIORITY,
} from '../../../PhotoSearchCheckout/helpers';
import SearchSubscriptionPlanOverview from '../SearchSubscriptionPlanOverview';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../../components/global/StripeCheckout';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../../constants/account-referrals';
import {
  searchSubscriptionCancel as searchSubscriptionCancelMutation,
  searchSubscriptionRestore as searchSubscriptionRestoreMutation,
} from '../../../../../../../graphql/mutations/checkout';
import { AccountPhotoSearchCheckout } from '../../../../../../../graphql/queries/account';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import Spinner from '../../../../../../../components/global/Spinner';
import ArrowRight from '../../../../../../../components/icons/ArrowRight';
import Button from '../../../../../../../components/global/Button';
import { PHOTO_SEARCH_FEATURE_COMPARISON } from '../PhotoSearchFeatureComparisonModal/PhotoSearchFeatureComparisonModal';
import { sortSubscriptionsByBillingPeriod } from '../SearchSubscriptionFeatureUpsell/helpers';
import {
  BASIC_ACCOUNT_MAX_FREE_EVENTS,
  BASIC_ACCOUNT_MAX_STORAGE,
} from '../../constants';
import {
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../../../../../constants/albums';

const propTypes = {
  className: PropTypes.string,
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  selectedSearchItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onCheckout: PropTypes.func.isRequired,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    search_subscription_free_trial_enabled: PropTypes.bool.isRequired,
  }),
  submitting: PropTypes.bool,
  activeSearchSubscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_period: PropTypes.string.isRequired,
    payment_processor: PropTypes.string.isRequired,
    plan: PropTypes.string.isRequired,
    expires_at: PropTypes.string.isRequired,
    trial_days: PropTypes.number,
  }).isRequired,
  searchSubscriptionCancel: PropTypes.func.isRequired,
  searchSubscriptionRestore: PropTypes.func.isRequired,
  freePlanHidden: PropTypes.bool,
  forSelection: PropTypes.bool,
  onSelect: PropTypes.func,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    album_type: PropTypes.oneOf(Object.keys(ALBUM_TYPE)),
    album_subtype: PropTypes.oneOf(Object.keys(ALBUM_SUBTYPE)),
  }),
};

const PhotoSearchSubscriptionSelection = ({
  className,
  searchSubscriptionItems,
  onCheckout,
  account,
  submitting,
  activeSearchSubscription,
  searchSubscriptionCancel,
  searchSubscriptionRestore,
  freePlanHidden = false,
  forSelection = false,
  onSelect,
  albumFolder,
}) => {
  const classes = classNames({
    [className]: !!className,
  });
  const [cancelling, setCancelling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(
    SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS,
  );
  const openModal = useOpenModal();
  const requestConfirm = useRequestConfirm();
  const openAlert = useOpenAlert();

  const hasPaymentMethod = !!account?.default_payment_method?.id;

  const freePlan = getFreePlan();
  const activeSubscriptionSearchSubscriptionItem = searchSubscriptionItems.find(
    ({ plan, billing_period: billing }) =>
      !!activeSearchSubscription &&
      plan === activeSearchSubscription?.plan &&
      billing === activeSearchSubscription?.billing_period,
  );

  const onCancel = () => {
    const overBasicStorage = account?.storage?.used > BASIC_ACCOUNT_MAX_STORAGE;

    if (overBasicStorage) {
      requestConfirm({
        message: (
          <Fragment>
            <h4 className="w-100 text-center">
              Are you sure you want to cancel your subscription?
            </h4>
            <p className="text-bold text-center">
              You own {pluralize('event', account.album_folders.total, true)}{' '}
              and are using {account.storage.used}GB of storage. Free accounts
              are limited to {BASIC_ACCOUNT_MAX_FREE_EVENTS} free events and{' '}
              {BASIC_ACCOUNT_MAX_STORAGE}GB of storage.
            </p>
            <p className="text-bold text-center">
              Without your subscription, you will not be able to create new
              events and the most recent{' '}
              <span className="text-danger text-extra-bold">
                {account.storage.used - BASIC_ACCOUNT_MAX_STORAGE}GB
              </span>{' '}
              of content will be deleted in 30 days.
            </p>
          </Fragment>
        ),
        confirmText: 'Yes, cancel my subscription',
        confirmBtnStyle: 'danger',
        confirmBtnOutline: true,
        cancelText: 'No, keep my subscription',
        cancelBtnStyle: 'primary',
        btnContainerClassName: 'flex-sm-row-reverse',
        cancelBtnClassName: 'ml-sm-2 mr-0',
        onConfirm: async () => {
          setCancelling(true);
          try {
            await searchSubscriptionCancel(activeSearchSubscription.id);
          } catch (error) {
            openAlert({
              type: 'error',
              message: getErrorString(error),
            });
          } finally {
            setCancelling(false);
          }
        },
        onCancel: () => {},
      });
    } else {
      requestConfirm({
        message: (
          <Fragment>
            <h4 className="w-100 text-center">Downgrade your subscription?</h4>
            <p className="text-bold">
              You are about to downgrade your account from Waldo{' '}
              {activeSearchSubscription.plan ===
              SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM
                ? 'Premium'
                : 'Plus'}{' '}
              to Waldo Basic. Any content in excess of 10GB of storage will need
              to be removed. You will not be able to create new events, and will
              no longer receive automated photo delivery, 15% discounts, or
              access to Waldo&apos;s slideshow builder. Are you sure you want to
              Downgrade to Waldo Basic?
            </p>
            <p className="text-bold">
              Your subscription expires on{' '}
              {moment(activeSearchSubscription.expires_at).format(
                'MMMM Do, YYYY',
              )}
              .
            </p>
          </Fragment>
        ),
        confirmText: 'Downgrade to Waldo Basic (Free)',
        cancelText: 'Go Back',
        onConfirm: async () => {
          setCancelling(true);
          try {
            await searchSubscriptionCancel(activeSearchSubscription.id);
          } catch (error) {
            openAlert({
              type: 'error',
              message: getErrorString(error),
            });
          } finally {
            setCancelling(false);
          }
        },
        onCancel: () => {},
      });
    }
  };

  const onRestore = async () => {
    requestConfirm({
      message: (
        <Fragment>
          <h4 className="w-100 text-center">Restore your subscription?</h4>
          <p className="text-bold">
            You are about to restore your plan. Are you sure you want to do
            this?
          </p>
          <p className="text-bold">
            You next billing will happen on{' '}
            {moment(activeSearchSubscription.expires_at).format(
              'MMMM Do, YYYY',
            )}
            .
          </p>
        </Fragment>
      ),
      confirmText: 'Restore my subscription',
      cancelText: 'Go Back',
      onConfirm: async () => {
        setRestoring(true);
        try {
          await searchSubscriptionRestore(activeSearchSubscription.id);
        } catch (error) {
          openAlert({
            type: 'error',
            message: getErrorString(error),
          });
        } finally {
          setRestoring(false);
        }
      },
      onCancel: () => {},
    });
  };

  const onCheckoutSearchSubscriptionItem = ({ searchSubscriptionItem }) => {
    // We should show checkout on both cases, either  we have no payment method, or we're upgrading to a higher tier
    if (
      !hasPaymentMethod ||
      !activeSearchSubscription ||
      searchSubscriptionItem.billing_period ===
        SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
    ) {
      openModal(STRIPE_CHECKOUT_MODAL, {
        checkoutPriceVariables: ({ promo_code: promoCode }) => {
          return {
            search_subscription_items: [
              {
                search_subscription_item_id: searchSubscriptionItem?.id,
              },
            ],
            promo_code: promoCode,
          };
        },
        accountReferralTypes: [
          ACCOUNT_REFERRAL_TYPE.SHUTTERBUG_PROMO,
          ACCOUNT_REFERRAL_TYPE.TIER_1_SHUTTERBUG_PROMO,
          ACCOUNT_REFERRAL_TYPE.PLUS_MONTHLY,
          ACCOUNT_REFERRAL_TYPE.PLUS_YEARLY,
        ],
        onSuccess: token =>
          onCheckout({
            searchSubscriptionItem,
            token,
          }),
        defaultName: account?.name ?? '',
        defaultEmail: account?.email_address ?? '',
      });
    } else {
      onCheckout({
        searchSubscriptionItem,
        stripePaymentMethodId: account.default_payment_method.id,
        token: {
          email: account?.email_address ?? '',
        },
      });
    }
  };

  const onPurchase = ({ searchSubscriptionItem }) => {
    if (forSelection && onSelect) {
      onSelect({ searchSubscriptionItem });
      return;
    }

    // This means it's an actual plan:
    if (activeSearchSubscription && !searchSubscriptionItem?.plan) {
      onCancel();
      return;
    }

    if (
      activeSearchSubscription?.plan === searchSubscriptionItem?.plan &&
      activeSearchSubscription?.billing_period ===
        searchSubscriptionItem?.billing_period &&
      !activeSearchSubscription.cancelled_at
    ) {
      onCancel();
      return;
    }

    if (
      activeSearchSubscription?.cancelled_at &&
      activeSearchSubscription?.plan === searchSubscriptionItem?.plan &&
      activeSearchSubscription?.billing_period ===
        searchSubscriptionItem?.billing_period
    ) {
      onRestore();
      return;
    }

    const [name, plan] = formatEnumValue(searchSubscriptionItem.plan).split(
      ' ',
    );

    if (searchSubscriptionItem?.plan) {
      if (
        activeSearchSubscription &&
        SEARCH_SUBSCRIPTION_PLAN_PRIORITY[activeSearchSubscription.plan] <
          SEARCH_SUBSCRIPTION_PLAN_PRIORITY[searchSubscriptionItem.plan]
      ) {
        requestConfirm({
          message: (
            <Fragment>
              <h4 className="w-100 text-center">Upgrade your subscription?</h4>
              <p className="text-bold">
                You are about to upgrade your plan to{' '}
                <strong>
                  {name} {plan}
                </strong>
                . Are you sure you want to do this?
              </p>
            </Fragment>
          ),
          confirmText: 'Yes, upgrade',
          cancelText: 'Go Back',
          onConfirm: async () => {
            onCheckoutSearchSubscriptionItem({ searchSubscriptionItem });
          },
          onCancel: () => {},
        });
      } else {
        onCheckoutSearchSubscriptionItem({ searchSubscriptionItem });
      }
    }
  };

  const enabledPlans =
    activeSearchSubscription?.plan ===
    SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM
      ? [
          SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS,
          SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM,
        ]
      : [SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS];

  const planOptionClasses = classNames({
    'photo-search-upsell-plan-options': true,
    'photo-search-upsell-plan-options-two-grid': enabledPlans.length === 1,
    'photo-search-upsell-plan-options-single-grid':
      freePlanHidden && enabledPlans.length === 1,
  });

  return (
    <div className={classes}>
      {(submitting || restoring || cancelling) && (
        <div className="d-flex flex-column justify-content-center align-items-center my-2">
          <span className="text-primary mb-2 photo-search-processing-text">
            {submitting && <>Processing your request&hellip;</>}
            {cancelling && <>Cancelling your plan&hellip;</>}
            {restoring && <>Restoring your plan&hellip;</>}
          </span>
          <Spinner size="sm" className="m-auto " />
        </div>
      )}

      <div className={planOptionClasses}>
        {enabledPlans.map(plan => {
          return (
            <SearchSubscriptionPlanOverview
              key={plan}
              account={account}
              searchSubscriptionItem={
                activeSubscriptionSearchSubscriptionItem?.plan === plan
                  ? activeSubscriptionSearchSubscriptionItem
                  : undefined
              }
              searchSubscriptionItems={sortSubscriptionsByBillingPeriod(
                searchSubscriptionItems.filter(item => item.plan === plan),
                SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY,
              )}
              onSelected={() => setSelectedPlan(plan)}
              selected={!activeSearchSubscription && plan === selectedPlan}
              activeSearchSubscription={activeSearchSubscription}
              onPurchase={onPurchase}
              submitting={submitting || cancelling || restoring}
              checkoutBtnText={forSelection ? 'Select' : undefined}
              albumFolder={albumFolder}
            />
          );
        })}
        {!freePlanHidden && (
          <SearchSubscriptionPlanOverview
            key="free"
            account={account}
            searchSubscriptionItem={freePlan}
            onSelected={() => setSelectedPlan(freePlan?.plan)}
            selected={
              !activeSearchSubscription && freePlan.plan === selectedPlan
            }
            activeSearchSubscription={activeSearchSubscription}
            onCheckout={onCheckout}
            submitting={submitting}
            checkoutBtnText={(() => {
              if (
                activeSearchSubscription &&
                !activeSearchSubscription?.cancelled_at
              ) {
                return 'Downgrade';
              }

              if (
                activeSearchSubscription &&
                activeSearchSubscription?.cancelled_at
              ) {
                return `Active on ${moment(
                  activeSearchSubscription.expires_at,
                ).format('MM/DD/YYYY')}`;
              }

              return undefined;
            })()}
            checkoutBtnProps={{
              btnOutline:
                activeSearchSubscription &&
                !activeSearchSubscription?.cancelled_at,

              disabled: !!activeSearchSubscription?.cancelled_at,
            }}
            onPurchase={onPurchase}
          />
        )}
      </div>

      {!freePlanHidden && (
        <Button
          btnSize="sm"
          btnStyle="link"
          className="mt-3 mx-auto d-flex photo-search-view-features"
          onClick={() => openModal(PHOTO_SEARCH_FEATURE_COMPARISON)}
        >
          View and compare all features{' '}
          <ArrowRight color="#0d84fe" size={12} className="ml-1" />
        </Button>
      )}
    </div>
  );
};

PhotoSearchSubscriptionSelection.propTypes = propTypes;

const withSearchSubscriptionCancel = graphql(searchSubscriptionCancelMutation, {
  props: ({ mutate }) => ({
    searchSubscriptionCancel: id =>
      mutate({
        variables: {
          input: {
            search_subscription_id: id,
          },
        },
        refetchQueries: [
          {
            query: AccountPhotoSearchCheckout,
          },
        ],
      }),
  }),
});

const withSearchSubscriptionRestore = graphql(
  searchSubscriptionRestoreMutation,
  {
    props: ({ mutate }) => ({
      searchSubscriptionRestore: id =>
        mutate({
          variables: {
            input: {
              search_subscription_id: id,
            },
          },
          refetchQueries: [
            {
              query: AccountPhotoSearchCheckout,
            },
          ],
        }),
    }),
  },
);

export default compose(
  withSearchSubscriptionCancel,
  withSearchSubscriptionRestore,
)(PhotoSearchSubscriptionSelection);
